import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { Course } from '../../types';
import { parseCourseMetadataInput } from '../../utils/courseMetadata';
import Navbar from '../shared/Navbar';

const ManageCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<{ authenticated: boolean; user?: { email: string; is_admin: boolean } } | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    detailed_description: '',
    subjects: '',
    levels: '',
    tags: '',
    hidden: false,
  });
  const [creating, setCreating] = useState(false);

  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getAdminCourses();
      setCourses(data.courses);
    } catch (e) {
      setError('Failed to load courses');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const current = await api.me();
        setMe(current);
        if (!(current?.authenticated && current?.user?.is_admin)) {
          setLoading(false);
          return;
        }
        await loadCourses();
      } catch (e) {
        setError('Failed to load courses');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    void bootstrap();
  }, [loadCourses]);

  const handleDelete = useCallback(async (courseId: number) => {
    if (!window.confirm('Delete this course and all its lessons? This cannot be undone.')) return;
    try {
      setDeleting(courseId);
      const res = await api.deleteCourse(courseId);
      if (!res.success) {
        alert('Failed to delete course');
      }
      await loadCourses();
    } catch (e: any) {
      alert(e?.message || 'Failed to delete course');
    } finally {
      setDeleting(null);
    }
  }, [loadCourses]);

  const handleCreate = async () => {
    if (!newCourse.title.trim()) {
      alert('Title is required');
      return;
    }
    try {
      setCreating(true);
      await api.createCourse({
        ...newCourse,
        subjects: parseCourseMetadataInput(newCourse.subjects),
        levels: parseCourseMetadataInput(newCourse.levels),
        tags: parseCourseMetadataInput(newCourse.tags),
      });
      setNewCourse({
        title: '',
        description: '',
        detailed_description: '',
        subjects: '',
        levels: '',
        tags: '',
        hidden: false,
      });
      setShowCreateModal(false);
      await loadCourses();
    } catch (e: any) {
      alert(e?.message || 'Failed to create course');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container">
          <div className="page-intro">
            <h2>Manage Courses</h2>
            <p>Add, edit, or delete courses</p>
          </div>
          {!(me?.authenticated && me?.user?.is_admin) && (
            <div className="admin-access-warning">
              <div className="tm-label">Admin access required</div>
              <div className="tm-mb-sm">You must be signed in as an admin to manage courses.</div>
              <div className="tm-actions">
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/login">Sign In</Link>
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/">Back to Home</Link>
              </div>
            </div>
          )}

          {me?.authenticated && me?.user?.is_admin && (
            <>
              <div className="tm-toolbar">
                <button className="tm-button tm-button--primary ml-replace-btn" onClick={() => setShowCreateModal(true)}>
                  ➕ New Course
                </button>
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/">← Back to Home</Link>
              </div>

              <div className="manage-lessons-list">
                <div className="ml-header">
                  <h3>Courses</h3>
                  {loading && <span className="ml-subtle">Loading…</span>}
                  {error && <span className="ml-error">{error}</span>}
                </div>

                <div className="ml-items">
                  {courses.length === 0 && !loading ? (
                    <div className="ml-empty">No courses yet. Create one to get started.</div>
                  ) : (
                    courses.map((course) => (
                      <div key={course.id} className="ml-item">
                        <div className="ml-item-row">
                          <div className="ml-item-main">
                            <div className="ml-title">
                              {course.title}
                              {course.hidden && <span className="course-hidden-badge">Hidden</span>}
                            </div>
                            <div className="ml-desc">{course.description}</div>
                            <div className="ml-meta tm-meta">
                              {course.subjects.length > 0 && <span>Subjects: {course.subjects.join(', ')} • </span>}
                              {course.levels.length > 0 && <span>Levels: {course.levels.join(', ')} • </span>}
                              {course.tags.length > 0 && <span>Tags: {course.tags.join(', ')} • </span>}
                              <span>{course.lesson_count || 0} lesson{(course.lesson_count || 0) === 1 ? '' : 's'}</span>
                            </div>
                          </div>
                          <div className="ml-item-controls">
                            <button
                              className="tm-button tm-button--primary ml-replace-btn"
                              onClick={() => navigate(`/admin/courses/${course.id}`)}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              className="tm-button tm-button--danger ml-delete-btn"
                              onClick={() => handleDelete(course.id)}
                              disabled={deleting === course.id}
                            >
                              {deleting === course.id ? 'Deleting…' : '🗑️ Delete'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}

          {showCreateModal && (
            <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Create New Course</h3>
                  <button className="close" onClick={() => setShowCreateModal(false)}>×</button>
                </div>
                <div className="modal-body">
                  <div className="tm-stack">
                    <div>
                      <label className="tm-label">Title *</label>
                      <input
                        type="text"
                        value={newCourse.title}
                        onChange={(e) => setNewCourse(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Course title"
                        className="tm-input"
                      />
                    </div>
                    <div>
                      <label className="tm-label">Description</label>
                      <input
                        type="text"
                        value={newCourse.description}
                        onChange={(e) => setNewCourse(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Short description"
                        className="tm-input"
                      />
                    </div>
                    <div>
                      <label className="tm-label">Detailed Description</label>
                      <textarea
                        value={newCourse.detailed_description}
                        onChange={(e) => setNewCourse(prev => ({ ...prev, detailed_description: e.target.value }))}
                        placeholder="Detailed description of the course"
                        rows={3}
                        className="tm-input"
                      />
                    </div>
                    <div className="tm-actions">
                      <div className="tm-flex-fill">
                        <label className="tm-label">Subjects</label>
                        <input
                          type="text"
                          value={newCourse.subjects}
                          onChange={(e) => setNewCourse(prev => ({ ...prev, subjects: e.target.value }))}
                          placeholder="Comma-separated subjects"
                          className="tm-input"
                        />
                      </div>
                      <div className="tm-flex-fill">
                        <label className="tm-label">Levels</label>
                        <input
                          type="text"
                          value={newCourse.levels}
                          onChange={(e) => setNewCourse(prev => ({ ...prev, levels: e.target.value }))}
                          placeholder="Comma-separated levels"
                          className="tm-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="tm-label">Tags</label>
                      <input
                        type="text"
                        value={newCourse.tags}
                        onChange={(e) => setNewCourse(prev => ({ ...prev, tags: e.target.value }))}
                        placeholder="e.g., exam-prep, featured"
                        className="tm-input"
                      />
                    </div>
                    <label className="tm-check-label">
                      <input
                        type="checkbox"
                        checked={newCourse.hidden}
                        onChange={(e) => setNewCourse(prev => ({ ...prev, hidden: e.target.checked }))}
                      />
                      Hide this course from the public catalogue
                    </label>
                  </div>
                </div>
                <div className="modal-footer tm-actions tm-actions--end">
                  <button className="tm-button tm-button--danger ml-delete-btn" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button className="tm-button tm-button--primary ml-replace-btn" onClick={handleCreate} disabled={creating}>
                    {creating ? 'Creating…' : 'Create Course'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManageCoursesPage;

